// LOS CÓDIGOS DE LA TERMINAL DE PRODUCTO, en JS puro · contrato v2 §2.1.
//
// El código de usuario (6), el código de terminal (6) y el nonce de turno
// (8) salen del mismo alfabeto de 31 símbolos: sin 0 ni O, sin 1, I ni L,
// porque en una pantalla de 128×64 se confunden. El servidor los compara
// sin distinguir mayúsculas, guiones ni espacios; aquí se hace lo mismo
// ANTES de mandar nada, para que la persona vea lo que se va a enviar.
import { useEffect, useReducer } from 'react';

export const ALFABETO = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const LARGO_CODIGO = 6;
export const LARGO_NONCE = 8;
export const LARGO_NOMBRE = 21;

const CONFUNDIBLES = new Set(['0', 'O', '1', 'I', 'L']);

/**
 * Lo que se teclea, convertido a lo que la terminal entiende:
 * mayúsculas, sin guiones ni espacios, recortado a `largo`. Lo que no
 * está en el alfabeto no entra y se devuelve en `rechazados`, para decir
 * por qué desapareció.
 */
export function normalizarCodigo(texto, largo = LARGO_CODIGO) {
  const crudo = String(texto ?? '').toUpperCase().replace(/[\s-]+/g, '');
  let codigo = '';
  const rechazados = [];
  for (const c of crudo) {
    if (ALFABETO.includes(c)) {
      if (codigo.length < largo) codigo += c;
    } else if (!rechazados.includes(c)) {
      rechazados.push(c);
    }
  }
  return { codigo, rechazados };
}

/** Por qué no entró un carácter, en una frase. */
export function motivoDeRechazo(rechazados) {
  if (!rechazados?.length) return null;
  const confundible = rechazados.find((c) => CONFUNDIBLES.has(c));
  if (confundible) {
    return `No se admite «${confundible}»: el código no lleva 0, O, 1, I ni L, que se `
      + 'confunden entre sí. Mira la pantalla de la terminal y teclea el carácter que se le parece.';
  }
  return `No se admite «${rechazados[0]}»: el código solo lleva letras y números.`;
}

/** `K7P4QX` → `K7P-4QX`, como lo pinta la terminal. */
export function formatearCodigo(codigo) {
  const c = String(codigo ?? '');
  return c.length === LARGO_CODIGO ? `${c.slice(0, 3)}-${c.slice(3)}` : c;
}

export const codigoCompleto = (codigo) => String(codigo ?? '').length === LARGO_CODIGO;

/** Un nombre de estación cabe en la pantalla de la terminal: 21. */
export function recortarNombre(texto) {
  return String(texto ?? '').replace(/\s+/g, ' ').trimStart().slice(0, LARGO_NOMBRE);
}

/**
 * Qué falta en «dónde va a trabajar», o null si ya está todo. Una
 * terminal es UNA estación (R-TP5): la repisa es obligatoria.
 */
export function faltaEnDonde({ id_ubicacion, nombre }, ubicaciones) {
  if (!ubicaciones?.length) return 'No hay repisas registradas: crea una en Ubicaciones y vuelve aquí.';
  if (!id_ubicacion) return 'Elige la repisa donde va a trabajar.';
  if (!recortarNombre(nombre).trim()) return 'Ponle un nombre a la terminal.';
  return null;
}

/** Segundos que faltan para `venceEn` (ISO); 0 si ya pasó; null sin fecha. */
export function segundosRestantes(venceEn, ahora = Date.now()) {
  if (!venceEn) return null;
  const t = Date.parse(venceEn);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((t - ahora) / 1000));
}

/** `605` → `10:05`. */
export function minutosYSegundos(segundos) {
  const s = Math.max(0, Number(segundos) || 0);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Cuenta regresiva de un segundo en un segundo hasta `venceEn`. El valor
 * se CALCULA en cada pintado a partir del reloj; el efecto solo marca el
 * paso de cada segundo, así no hay estado que sincronizar con la fecha.
 */
export function useCuentaRegresiva(venceEn) {
  const [, tic] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    if (!venceEn) return undefined;
    const t = setInterval(tic, 1000);
    // En Node (las pruebas) el reloj no debe retener el proceso.
    if (t && typeof t.unref === 'function') t.unref();
    return () => clearInterval(t);
  }, [venceEn]);
  return segundosRestantes(venceEn);
}
